import React, { useEffect, useRef } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { Course } from '../services/supabase';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

interface CourseMapProps {
  courses: Course[];
  userLocation: { lat: number; lng: number } | null;
}

/** Manages imperative google.maps.Marker instances on the declarative Map. */
function MapMarkers({ courses, userLocation }: CourseMapProps) {
  const map = useMap();
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!map) return;

    // Clear old markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (!infoRef.current) {
      infoRef.current = new google.maps.InfoWindow();
    }
    const info = infoRef.current;

    // Course markers
    courses.forEach(course => {
      const color = course.par3_holes > 0 ? '#9CB75A' : '#9E9E9E';
      const marker = new google.maps.Marker({
        position: { lat: course.latitude, lng: course.longitude },
        map,
        title: course.name,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
            `<svg width="20" height="32" viewBox="0 0 20 32" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 0C4.5 0 0 4.5 0 10c0 10 10 22 10 22s10-12 10-22c0-5.5-4.5-10-10-10z" fill="${color}" stroke="#ffffff" stroke-width="1"/>
              <circle cx="10" cy="10" r="3" fill="white"/>
            </svg>`
          )}`,
          scaledSize: new google.maps.Size(20, 32),
          anchor: new google.maps.Point(10, 32),
        },
      });

      marker.addListener('click', () => {
        info.setContent(
          `<div style="padding:8px;min-width:200px">
            <h3 style="font-weight:bold;font-size:16px;color:#111827;margin:0 0 4px">${course.name}</h3>
            <p style="color:#6b7280;margin:0 0 4px">${course.location}</p>
            <p style="color:#9ca3af;font-size:14px;margin:0">${course.par3_holes} Par 3 Holes</p>
          </div>`
        );
        info.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    // User location marker
    if (userLocation) {
      const userMarker = new google.maps.Marker({
        position: userLocation,
        map,
        title: 'Your Location',
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
            `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#2196F3" stroke="#ffffff" stroke-width="2"/>
              <circle cx="12" cy="12" r="4" fill="white"/>
            </svg>`
          )}`,
          scaledSize: new google.maps.Size(24, 24),
          anchor: new google.maps.Point(12, 12),
        },
      });

      userMarker.addListener('click', () => {
        info.setContent(
          `<div style="padding:8px;min-width:200px">
            <h3 style="font-weight:bold;font-size:16px;color:#111827;margin:0 0 4px">Your Location</h3>
            <p style="color:#6b7280;margin:0">You are here</p>
          </div>`
        );
        info.open(map, userMarker);
      });

      markersRef.current.push(userMarker);
    }

    return () => {
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
    };
  }, [map, courses, userLocation]);

  return null;
}

/** Fits the map bounds to show courses + user location. */
function MapBoundsHandler({ courses, userLocation }: CourseMapProps) {
  const map = useMap();

  useEffect(() => {
    if (!map || courses.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    if (userLocation) {
      bounds.extend(userLocation);
      // Only include 5 nearest courses (list is already sorted by distance)
      courses.slice(0, 5).forEach(c =>
        bounds.extend({ lat: c.latitude, lng: c.longitude })
      );
    } else {
      courses.forEach(c =>
        bounds.extend({ lat: c.latitude, lng: c.longitude })
      );
    }

    map.fitBounds(bounds);

    // Prevent excessive zoom-out when user location is present
    if (userLocation) {
      const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
        const z = map.getZoom();
        if (z !== undefined && z !== null && z < 10) map.setZoom(10);
      });
      return () => google.maps.event.removeListener(listener);
    }
  }, [map, courses, userLocation]);

  return null;
}

/** Top-level map wrapper. Renders fallback if no API key. */
export default function CourseMap({ courses, userLocation }: CourseMapProps) {
  if (!API_KEY) {
    return (
      <div className="rounded-2xl h-[500px] w-full bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl"
            style={{ backgroundColor: '#9CB75A' }}
          >
            &#9971;
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Interactive Map</h3>
          <p className="text-gray-500 text-sm">
            Map temporarily unavailable. Course locations are shown in the list below.
          </p>
        </div>
      </div>
    );
  }

  const defaultCenter = userLocation || { lat: 40.7608, lng: -111.891 };
  const defaultZoom = userLocation ? 12 : 10;

  return (
    <APIProvider apiKey={API_KEY}>
      <div className="rounded-2xl h-[500px] w-full overflow-hidden">
        <Map
          defaultCenter={defaultCenter}
          defaultZoom={defaultZoom}
          gestureHandling="cooperative"
          disableDefaultUI={false}
          style={{ width: '100%', height: '100%' }}
        >
          <MapMarkers courses={courses} userLocation={userLocation} />
          <MapBoundsHandler courses={courses} userLocation={userLocation} />
        </Map>
      </div>
    </APIProvider>
  );
}
